import FormData from 'form-data';

import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import ProgressBar from 'components/global/ProgressBar';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { FORM } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faExclamationCircle, faFileCirclePlus, faXmark } from '@fortawesome/pro-solid-svg-icons';

import Button from './Button';
import * as S from './styled/ImageInput.styled';
import { InputMessageContainer } from './styled/InputCommon.styled';

/**
 * @author Burak Ak
 * @description File input component.
 * @param {Object} props - props of the component.
 * @param {string} [props.name] - name of the input field.
 * @param {string} [props.uploadURL] - uploadURL of the input field.
 * @param {string} [props.additionalParams] - additionalParams of which will be sent to the server.
 * @param {Object} [props.value] - value prop form the form
 * @param {Object} [props.setValue] - setValue prop form the form
 * @param {boolean} [props.disabled = false] - If true, the button will be disabled.
 * @param {Object} [props.acceptedFileType] - Accepted file type will only be uploaded.
 * @param {Object} [props.style] - Custom css styles for progress bar by inline jsx styling. style={{backgroundColor: "lightblue"}}
 * @param {Object} [props.btnStyle] - Custom css styles for progress bar by inline jsx styling. style={{backgroundColor: "lightblue"}}
 * @param {string} [props.maxFileSize] - Max file size is to limit the file size
 * @param {*} [props.children] - The content of the component. If it's empty only label and icon will be shown.
 * @returns {object} - Button object with the given props.
 * @example

 *
 * <FileInput
 * 	acceptedFileType="xls"
 * 	maxFileSize="500000"
 * 	disabled="false"
 * />
 */
const FileInput = ({
	additionalParams = {},
	uploadUrl = null,
	acceptedFileType = '*',
	imageHeight,
	maxFileSize,
	disabled = false,
	setValue,
	style = '',
	btnStyle = {},
	name,
	errorMessage: errorMessageProp
}) => {
	const { t } = useTranslation();
	const [uploadedUrl, setUploadedUrl] = useState(null);
	const [uploadPercentage, setUploadPercentage] = useState(0);
	const [selectedFile, setSelectedFile] = useState(null);
	const [message, setMessage] = useState(null);
	const { showSuccess, showError } = useAlert();
	const [isLoading, setIsLoading] = useState(false);
	const axiosPrivate = useAxiosPrivate();

	const isImage = (file) => file && file.type.split('/')[0] === 'image';
	const isAcceptedFileType = (file) => {
		const acceptedFileTypeArray = acceptedFileType?.split(',');

		return (
			(file && acceptedFileTypeArray.includes(file.type.split('/')[1])) ||
			acceptedFileTypeArray.includes(file.name.split('.')[1])
		);
	};

	const onRemoveFile = () => {
		if (disabled) return; // block click when disabled

		setValue(null);
		setSelectedFile(null);
		setUploadedUrl(null);
		setMessage(null);
	};

	const onFileChange = async (event) => {
		if (disabled) return; // block click when disabled

		try {
			setIsLoading(true);

			setMessage(null);

			setUploadPercentage(0);

			setSelectedFile(event?.target.files[0]);

			if (event?.target.files[0].size > maxFileSize) {
				showError({ header: 'fileSize' });
				setMessage(t('invalidFileSize'));
				setIsLoading(false);
				setValue(null, 'invalidFileSize');
				return;
			}

			if (isImage(event?.target.files[0])) {
				const imageFile = event.target.files[0];
				imageFile.objectURL = URL.createObjectURL(imageFile);
				setSelectedFile(imageFile);
			}

			const formData = new FormData();

			formData.append('file', event?.target.files[0]);
			formData.append('name', event?.target.files[0].name);
			formData.append('type', 'import');

			Object.keys(additionalParams)?.forEach((key) => {
				formData.append(key, additionalParams[key]);
			});

			const config = {
				method: 'post',
				url: uploadUrl ?? FORM.UPLOAD_FILE,
				data: formData,
				headers: {
					'Content-Type': 'multipart/form-data'
				},
				transformRequest: () => formData,
				onUploadProgress: (progressEvent) => {
					setUploadPercentage(parseInt(Math.round((progressEvent.loaded * 100) / progressEvent.total)));
				}
			};

			const response = await axiosPrivate.request(config);

			const { path } = response.data;

			setUploadedUrl(path);
			setValue(path);
			showSuccess({ header: 'fileUploaded' });
		} catch (error) {
			setMessage('couldnt upload file');
			setUploadedUrl(null);

			AppLogger.error(error);
			if (error.response === 500) {
				showError({ header: 'serverError' });
			} else {
				showError({});
			}
		}
		setIsLoading(false);
	};

	const formMethods = useFormContext();
	const errorMessage = formMethods?.getFieldState?.(name, formMethods?.formState)?.error?.message ?? errorMessageProp;

	return (
		<S.FileInputFlexRow>
			{selectedFile ? (
				<S.LoadingContainer>
					<S.FileInputFlexRow>
						{isLoading && <ProgressBar value={uploadPercentage} style={style || null}></ProgressBar>}
						{isAcceptedFileType(selectedFile) && isImage(selectedFile) && (
							<Image
								url={selectedFile.objectURL}
								alt="uploaded"
								height={imageHeight ? imageHeight : '3rem'}
							/>
						)}
						<Typography className="fileName">
							{message ? message : <a href={uploadedUrl}>{selectedFile.name}</a>}
						</Typography>

						<Button variant="borderless" icon={faXmark} onClick={() => onRemoveFile()} iconButton />
					</S.FileInputFlexRow>
				</S.LoadingContainer>
			) : (
				<Button
					variant={errorMessage ? 'solid' : 'blackOutline'}
					color={errorMessage ? 'var(--red)' : undefined}
					component="label"
					label="upload"
					icon={faFileCirclePlus}
					className="file-input-button"
					style={{ ...btnStyle }}>
					<input
						accept={acceptedFileType}
						hidden
						onChange={(e) => {
							onFileChange(e);
						}}
						disabled={disabled}
						type="file"
						id="fileInput"
					/>
				</Button>
			)}
			{errorMessage && (
				<InputMessageContainer className="error-message-container file-input-error-message-container">
					<Icon icon={faExclamationCircle} color="var(--red)" size="x-small" />
					<Typography variant="caption" color="var(--red)" noWrap>
						{errorMessage}
					</Typography>
				</InputMessageContainer>
			)}
		</S.FileInputFlexRow>
	);
};

export default FileInput;
